import type { StaffAnalyticsAccess } from "@lafayette/schemas";
import { useParams } from "react-router";
import { useDocumentTitle } from "../../layout/document-title";
import { useWideLayout } from "../../layout/shell-layout";
import { AsyncState } from "../../ui/async-state";
import { CockpitHeader } from "../components/CockpitHeader";
import { PrivacyLine } from "../components/PrivacyLine";
import { RefusedState } from "../components/RefusedState";
import { TabBar } from "../components/TabBar";
import { EventsTab } from "../events/EventsTab";
import { useEventCountLabel } from "../events/events-queries";
import { ExploreTab } from "../explore/ExploreTab";
import { OverviewTab } from "../overview/OverviewTab";
import { type CustomerView, isRefusal, useCustomerOverview, useCustomerView } from "../queries";
import { useAnalyticsRuntime } from "../runtime";
import { useCockpitAddress } from "../url-state";

// `/analytics/:customerId?tab=&range=`: the cockpit of one customer (Paper "Analytics · Customer cockpit"). The page opens ONE audited view when
// it starts (and again when the customer or the tab changes), then reads inside it. A refusal has the same words for every cause.

export function CustomerCockpitPage() {
  useWideLayout();
  const customerId = useParams().customerId ?? "";
  const address = useCockpitAddress();
  const view = useCustomerView(customerId, address.tab);
  const overviewQuery = useCustomerOverview(view, customerId, address.range, address.tab);
  const access = overviewQuery.data?.access ?? view.opening.data?.access ?? null;
  const eventCount = useEventCountLabel(view, customerId, address.tab, address.range);
  useDocumentTitle("Customer analytics");
  const refused =
    isRefusal(view.opening.error) || (address.tab === "overview" && isRefusal(overviewQuery.error));
  return (
    <section aria-labelledby="page-title" className="grid gap-5">
      <CockpitHeader
        access={access}
        customerId={customerId}
        range={address.range}
        onRange={address.setRange}
      />
      {refused ? (
        <RefusedState />
      ) : (
        <>
          {access === null ? null : <PrivacyLine access={access} />}
          <TabBar current={address.tab} hrefOf={address.tabHref} eventCount={eventCount} />
          <TabBody
            customerId={customerId}
            view={view}
            access={access}
            address={address}
            overviewQuery={overviewQuery}
            openingFailed={view.opening.isError}
            retry={() => view.opening.refetch()}
          />
        </>
      )}
    </section>
  );
}

function TabBody({
  customerId,
  view,
  access,
  address,
  overviewQuery,
  openingFailed,
  retry,
}: Readonly<{
  customerId: string;
  view: CustomerView;
  access: StaffAnalyticsAccess | null;
  address: ReturnType<typeof useCockpitAddress>;
  overviewQuery: ReturnType<typeof useCustomerOverview>;
  openingFailed: boolean;
  retry: () => void;
}>) {
  const { timeZone } = useAnalyticsRuntime();
  if (openingFailed) {
    return (
      <AsyncState
        kind="error"
        title="The page could not be opened"
        message="Try again in a moment."
        onRetry={retry}
      />
    );
  }
  if (address.tab === "explore") {
    return (
      <ExploreTab
        customerId={customerId}
        range={address.range}
        view={view}
        tz={timeZone()}
        overviewHref={`?tab=overview&range=${address.range}`}
        eventsHref={(extra) =>
          `?${new URLSearchParams({ tab: "events", range: address.range, ...extra }).toString()}`
        }
      />
    );
  }
  if (address.tab === "events") {
    if (access === null) {
      return <AsyncState kind="loading" title="Loading the events" message="One moment." />;
    }
    return <EventsTab view={view} customerId={customerId} range={address.range} access={access} />;
  }
  if (overviewQuery.isError) {
    return (
      <AsyncState
        kind="error"
        title="The numbers could not be loaded"
        message="Nothing was changed. Try again in a moment."
        onRetry={() => overviewQuery.refetch()}
      />
    );
  }
  if (overviewQuery.data === undefined) {
    return <AsyncState kind="loading" title="Loading the numbers" message="One moment." />;
  }
  return (
    <OverviewTab
      overview={overviewQuery.data}
      range={address.range}
      view={view}
      customerId={customerId}
    />
  );
}
